import Joi from "joi";
import LoginResponse from "../models/api/LoginResponse";
import { TimeDiff, TimeIntervalFormat, TimeIntervalFormats } from "../models/Verification";

export const logout = () => {
  localStorage.removeItem('userToken');
  localStorage.removeItem('userId');
  window.location.href = '/';
};

export const loggedInToken = () => localStorage.getItem('userToken') || '';
export const loggedInId = () => localStorage.getItem('userId') || '';
export const loggedInEmail = () => localStorage.getItem('userEmail') || '';

export const login = (loginResponse: LoginResponse) => {
  localStorage.setItem('userId', loginResponse.userId);
  localStorage.setItem('userToken', loginResponse.token);
  localStorage.setItem('userEmail', loginResponse.email);
};

export const copyToClipboardAsync = (text: string) => {
  // currently only iOS > 13 supports native clipboard API. This is why we cannot use window.navigator.clipboard.writeText(text); yet
  const dummy = document.createElement('textarea');
  document.body.appendChild(dummy);
  dummy.value = text;
  dummy.select();
  document.execCommand('copy');
  document.body.removeChild(dummy);
}

export const userPostSchema = Joi.object({
  email: Joi.string().trim().email({ minDomainSegments: 2, tlds: { allow: false } }).required()
});

// TODO: this length is currently enforced by VUAS. According to Email RFC limit is 320 chars.
const EMAIL_MAX_LENGTH = 60;
const REGEX = "^[a-zA-Z0-9_+&*-]+(?:\\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\\.)+[a-zA-Z]{2,}$";

export const userPostSchemaFront = (t: Function) => Joi.object(getJoiObject(t, {
  email: Joi.string().pattern(new RegExp(REGEX)).max(EMAIL_MAX_LENGTH).required()
}))

export const userSignUpPostSchema = (t: Function) => Joi.object(getJoiObject(t,{
  email: Joi.string().pattern(new RegExp(REGEX)).max(EMAIL_MAX_LENGTH).required()
}));

export const companyPostSchema = (t: Function) => Joi.object(getJoiObject(t,{
  businessName: Joi.string().max(100).required(),
  businessColor: Joi.string(),
  businessLogo: Joi.string().allow(''),
  antispoofing: Joi.boolean(),
  callbackUrl: Joi.string().max(2048).allow('',null).default(null)
}));

export const contactPostSchema = (t: Function) => Joi.object(getJoiObject(t,{
  name: Joi.string(),
  country: Joi.string(),
  email: Joi.string().pattern(new RegExp(REGEX)).required(),
  message: Joi.string(),
}));

export const userDataFront = (t: Function) => Joi.object(getJoiObject(t, {
  name: Joi.string().required(),
  termsAndConditions: Joi.boolean()
}))

export const getJoiObject = (t: Function, object: any) =>
  Object.entries(object).reduce(
    (acc, [key, _]) => ({ ...acc, [key]: object[key].error((errors: Joi.ErrorReport[]) => translateSchema(errors, t) ) }),
    {}
  );

export const translateSchema = (errors: Joi.ErrorReport[], t: Function) =>   {
  console.log(errors);
  switch (errors[0].code) {
    case "string.empty":
      //@ts-ignore
      errors[0].message = t('validation.empty', { field: errors[0].local.key });
      break;
    case "string.pattern.base":
      //@ts-ignore
      errors[0].message = t('validation.pattern', { field: errors[0].local.key });
      break;
    case "string.min":
      //@ts-ignore
      errors[0].message = t('validation.min', { field: errors[0].local.key, min: errors[0].local.limit });
      break;
    case "string.max":
      //@ts-ignore
      errors[0].message = t('validation.max', { field: errors[0].local.key, max: errors[0].local.limit });
      break;
    default:
      //@ts-ignore
      errors[0].message = t('validation.generic');
      break;
  }
  return errors[0];
}

export const getTimeDiff = (str: string, isFuture?: boolean) => {
  const now = new Date().getTime()
  const then = new Date(str).getTime()
  const substract = !isFuture ? now - then : then - now
  let calc = Math.ceil(((substract) / 1000) / 60)
  let temp = 'minute'

  if (calc > 59) {
    calc = Math.floor(calc / 60)
    if (calc > 23) {
      calc = Math.floor(calc / 24)
      temp = 'day'
    } else {
      temp = 'hour'
    }
  }
  const plural = calc !== 1 ? 's' : ''
  const end = !isFuture ? ' ago' : ''

  return `${calc < 0 ? 0 : calc} ${temp}${plural}${end}`
}

export const getTimeDiffObj = (str: string, isFuture?: boolean): TimeDiff => {
  const now = new Date().getTime()
  const then = new Date(str).getTime()
  const substract = !isFuture ? now - then : then - now
  let calc = Math.ceil(((substract) / 1000) / 60)
  let temp: TimeIntervalFormat = TimeIntervalFormats.minutes

  if (calc > 59) {
    calc = Math.floor(calc / 60)
    if (calc > 23) {
      calc = Math.floor(calc / 24)
      temp = TimeIntervalFormats.days
    } else {
      temp = TimeIntervalFormats.hours
    }
  }

  return {
    value:  calc < 0 ? 0 : calc,
    time: temp,
    isFuture: !!isFuture,
    fullDate: str
  }
}

export const percentageTime = (expiresAt: string, createdAt: string) => {
  const now = new Date().getTime()
  const expiresMili = new Date(expiresAt).getTime()
  const createdMili = new Date(createdAt).getTime()
  const numb = +((expiresMili - now) / (expiresMili - createdMili)).toFixed(2)

  return (numb < 0) ? 0 : parseInt((numb* 100).toFixed(2))
}

export const getBase64FromBlob = async (blob: Blob) => {
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onload = resolve;
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export const formatExpirationTime = (time: number | undefined , t : any) => {
  let formatLinkExpiration = "";
  const mo = Math.floor(Number(time) / 43200); // 60*24*30
  const d = Math.floor((Number(time) - (mo * 43200)) / 1440); // 60*24
  const h = Math.floor((Number(time) - (mo * 43200) - (d * 1440)) / 60);
  const m = Math.round(Number(time) % 60);
  switch (time) {
    case 30:
      formatLinkExpiration = `30 ${ t('company.links.expires.minutes')}`;
      break;
    case 1440:
      formatLinkExpiration =`1 ${ t('company.links.expires.day')}`
      break;
    case 10080:
      formatLinkExpiration = `1 ${ t('company.links.expires.week')}`
      break
    case  86400:
      formatLinkExpiration = `2 ${ t('company.links.expires.months')}`
      break;
     default:
      if( m > 0){
        formatLinkExpiration = m + " " + t(`company.links.expires.minute${m === 1 ? '' : 's'}`)
      }
      if(h > 0){
        formatLinkExpiration = h + " " + t(`company.links.expires.hour${h === 1 ? '' : 's'}`)
      }
      if(d > 0){
        formatLinkExpiration = d + " " + t(`company.links.expires.day${d === 1 ? '' : 's'}`)
      }
      if(mo > 0){
        formatLinkExpiration = mo + " " + t(`company.links.expires.month${mo === 1 ? '' : 's'}`)
      }
      break;
  }
  return formatLinkExpiration;
}
